import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useCannotBecause } from '@/lib/permissions';
import { DownloadIcon, UploadIcon } from 'lucide-react';

import { ApiError, api, downloadFile, type CatalogueImportReport } from '@/lib/api';
import { useLabel, useT } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

/**
 * Catalogue export/import (`plan-beta` B4b).
 *
 * "Import/export of data" is broader than the item bank: getting a catalogue
 * from dev to staging, or a colleague's export into yours, needs modules,
 * personas and matrix cells to travel together — a cell without its persona is a
 * row that references nothing.
 *
 * IT LIVES IN SETTINGS, not on a catalogue screen, because it is an
 * environment-level act rather than an authoring one. Importing rewrites the
 * catalogue that every result in this environment was produced under, and it
 * should not sit one click from editing a prompt.
 *
 * THE DRY RUN IS THE DEFAULT AND THE SCREEN ENFORCES IT: the commit button is
 * unreachable until a dry run has come back clean. The reasoning behind what
 * counts as a conflict is in `services/catalogueBundle.ts`.
 */

const ACTION_TONE: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  create: 'default',
  update: 'secondary',
  skip: 'outline',
  conflict: 'destructive',
};

export function CataloguePortability() {
  const t = useT();
  // `portability.kind.*` and `portability.action.*` — both closed sets the
  // server decides, so they are labelled rather than printed raw.
  const label = useLabel();
  const [includeItems, setIncludeItems] = useState(false);
  const [bundle, setBundle] = useState<unknown>(null);
  const [filename, setFilename] = useState('');
  const [report, setReport] = useState<CatalogueImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * R3. Importing a bundle overwrites modules, personas and matrix cells
   * wholesale — the same governance act as publishing one, reached by a
   * different door, so it is `catalogue:publish` here and on the route.
   *
   * **Export is not gated.** Reading the catalogue out is exactly what an
   * analyst is for, and `GET /catalogue/export` has never required a role.
   */
  const cannotPublish = useCannotBecause('catalogue:publish');

  const exportIt = useMutation({
    mutationFn: () => api.exportCatalogue(includeItems),
    onSuccess: (b) => {
      downloadFile(
        `catalogue-${b.exportedAt.slice(0, 10)}.json`,
        JSON.stringify(b, null, 2),
        'application/json',
      );
      notify.success(
        t('portability.exported'),
        t('portability.exported.detail', {
          modules: b.modules.length,
          personas: b.personas.length,
          cells: b.matrixCells.length,
        })
          + (b.questions ? ` ${t('portability.exported.items', { items: b.questions.length })}` : '')
          + ` ${t('portability.exported.excluded')}`,
      );
    },
    onError: (err) =>
      notify.error(
        t('questions.export.failed'),
        err instanceof ApiError ? err.message : t('table.errorDescription'),
      ),
  });

  const importIt = useMutation({
    mutationFn: ({ dryRun }: { dryRun: boolean }) => api.importCatalogue(bundle, dryRun),
    onSuccess: (result) => {
      setReport(result);
      setError(null);
      if (!result.dryRun) {
        notify.success(
          t('portability.imported'),
          Object.entries(result.applied ?? {})
            .map(([kind, n]) => `${n} ${label('portability.kind', kind)}`)
            .join(', '),
        );
      }
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : t('portability.importFailed'));
      if (err instanceof ApiError) setReport(null);
    },
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setFilename(file.name);
    setReport(null);
    setError(null);
    try {
      const parsed = JSON.parse(await file.text());
      setBundle(parsed);
      importIt.mutate({ dryRun: true });
    } catch {
      setBundle(null);
      setError(t('portability.notJson'));
    }
  };

  const clean = report !== null && report.summary.conflict === 0 && report.dryRun;

  return (
    <div className="grid gap-8">
      {/* ------------------------------------------------------------ export */}
      <section className="grid max-w-xl gap-3">
        <h3 className="font-medium">{t('portability.export.title')}</h3>
        <p className="text-muted-foreground text-sm">{t('portability.export.description')}</p>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeItems}
            onChange={(e) => setIncludeItems(e.target.checked)}
          />
          {t('portability.includeItems')}
        </label>
        <Button
          className="w-fit"
          disabled={exportIt.isPending}
          onClick={() => exportIt.mutate()}
        >
          <DownloadIcon className="size-4" />
          {exportIt.isPending ? t('questions.exporting') : t('portability.exportAction')}
        </Button>
      </section>

      {/* ------------------------------------------------------------ import */}
      <section className="grid max-w-2xl gap-3">
        <h3 className="font-medium">{t('portability.import.title')}</h3>
        <p className="text-muted-foreground text-sm">{t('portability.import.description')}</p>

        <div className="grid gap-2">
          <Label htmlFor="bundleFile">{t('portability.bundleFile')}</Label>
          <input
            id="bundleFile"
            type="file"
            accept=".json,application/json"
            className="text-sm"
            onChange={(e) => void onFile(e.target.files?.[0])}
          />
          {filename && <p className="text-muted-foreground text-xs">{filename}</p>}
        </div>

        {importIt.isPending && (
          <p className="text-muted-foreground text-sm">{t('portability.checking')}</p>
        )}

        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {report && (
          <>
            <div className="flex flex-wrap gap-4 text-sm">
              <span>{t('portability.toCreate', { count: report.summary.create })}</span>
              <span>{t('portability.toUpdate', { count: report.summary.update })}</span>
              <span>{t('portability.unchanged', { count: report.summary.skip })}</span>
              <span className={report.summary.conflict ? 'text-destructive' : undefined}>
                {t('portability.conflicts', { count: report.summary.conflict })}
              </span>
            </div>
            <p className="text-muted-foreground text-xs">
              {t('portability.from', { source: report.source })}
            </p>

            {report.summary.conflict > 0 && (
              <Alert variant="destructive" role="alert">
                <AlertDescription>
                  <p className="mb-2">{t('portability.blocked')}</p>
                  <ul className="list-disc ps-4">
                    {report.plan.filter((p) => p.action === 'conflict').map((p) => (
                      <li key={`${p.kind}-${p.id}`}>
                        <span className="font-mono text-xs">{p.id}</span> — {p.detail}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="max-h-64 overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 text-start font-medium">{t('portability.column.kind')}</th>
                    <th className="p-2 text-start font-medium">{t('portability.column.id')}</th>
                    <th className="p-2 text-start font-medium">{t('portability.column.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.plan.slice(0, 200).map((p) => (
                    <tr key={`${p.kind}-${p.id}`} className="border-t">
                      <td className="p-2">{label('portability.kind', p.kind)}</td>
                      <td className="p-2 font-mono text-xs" dir="ltr">{p.id}</td>
                      <td className="p-2">
                        <Badge variant={ACTION_TONE[p.action] ?? 'outline'}>
                          {label('portability.action', p.action)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Button
              className="w-fit"
              disabled={!clean || importIt.isPending || cannotPublish !== null}
              title={cannotPublish ?? undefined}
              onClick={() => importIt.mutate({ dryRun: false })}
            >
              <UploadIcon className="size-4" />
              {t(report.dryRun ? 'portability.apply' : 'portability.applied')}
            </Button>
          </>
        )}
      </section>
    </div>
  );
}
